<script setup lang="ts">
import { computed } from "vue";
import { AddIcon, DeleteIcon, PlayIcon } from "../icons";
import { querySchema, type Field, type QuerySpec } from "@yawhub/core";
const props = defineProps<{ fields: Field[]; busy?: boolean }>(),
  emit = defineEmits(["run"]),
  query = defineModel<QuerySpec>({ required: true });
const eligible = computed(() =>
    props.fields.filter((f) => f.active && f.queryable),
  ),
  numerics = computed(() =>
    eligible.value.filter((f) =>
      ["integer", "decimal", "rating"].includes(f.type),
    ),
  );
function setMode(aggregate: boolean) {
  query.value = {
    ...querySchema.parse({}),
    filters: query.value.filters,
    period: query.value.period,
    metrics: aggregate ? [{ op: "count_records", alias: "antal" }] : [],
    dimensions: [],
  };
}
function addFilter() {
  const f = eligible.value[0];
  if (f) query.value.filters.push({ fieldId: f.id, op: "eq", value: "" });
}
</script>
<template>
  <div class="query-builder">
    <div class="query-top">
      <div class="segmented">
        <button
          type="button"
          :class="{ selected: !query.metrics.length }"
          @click="setMode(false)"
        >
          Rækker</button
        ><button
          type="button"
          :class="{ selected: !!query.metrics.length }"
          @click="setMode(true)"
        >
          Beregning
        </button>
      </div>
      <button
        type="button"
        class="button primary"
        :disabled="busy"
        @click="emit('run')"
      >
        <PlayIcon :size="16" />{{ busy ? "Beregner…" : "Kør forespørgsel" }}
      </button>
    </div>
    <div v-if="query.metrics[0]" class="form-grid query-metrics">
      <label
        >Måling<select v-model="query.metrics[0].op">
          <option value="count_records">Antal rækker</option>
          <option value="count_values">Antal udfyldte værdier</option>
          <option value="sum">Sum</option>
          <option value="avg">Gennemsnit</option>
          <option value="min">Minimum</option>
          <option value="max">Maksimum</option>
        </select></label
      ><label v-if="query.metrics[0].op !== 'count_records'"
        >Felt<select v-model="query.metrics[0].fieldId" required>
          <option :value="undefined" disabled>Vælg felt</option>
          <option
            v-for="f in ['sum', 'avg'].includes(query.metrics[0].op)
              ? numerics
              : eligible"
            :value="f.id"
            :key="f.id"
          >
            {{ f.label }}{{ f.unit ? " · " + f.unit : "" }}
          </option>
        </select></label
      ><label
        >Navn på resultat<input
          v-model="query.metrics[0].alias"
          placeholder="Fx sum"
          pattern="[a-zA-Z][a-zA-Z0-9_]*" /></label
      ><label
        >Gruppér efter<select
          :value="query.dimensions[0]?.fieldId ?? ''"
          @change="
            query.dimensions = ($event.target as HTMLSelectElement).value
              ? [{ fieldId: ($event.target as HTMLSelectElement).value }]
              : []
          "
        >
          <option value="">Ingen gruppering</option>
          <option v-for="f in eligible" :key="f.id" :value="f.id">
            {{ f.label }}
          </option>
        </select></label
      ><label
        v-if="
          query.dimensions[0] &&
          ['date', 'datetime'].includes(
            fields.find((f) => f.id === query.dimensions[0]?.fieldId)?.type ??
              '',
          )
        "
        >Tidsinterval<select v-model="query.dimensions[0].bucket">
          <option :value="undefined">Eksakt værdi</option>
          <option value="day">Dag</option>
          <option value="week">Uge</option>
          <option value="month">Måned</option>
        </select></label
      >
    </div>
    <div v-if="query.filters.length" class="filters">
      <div v-for="(filter, i) in query.filters" :key="i" class="filter-row">
        <select v-model="filter.fieldId" aria-label="Filterfelt">
          <option v-for="f in eligible" :value="f.id" :key="f.id">
            {{ f.label }}
          </option></select
        ><select v-model="filter.op" aria-label="Filteroperator">
          <option value="eq">er lig med</option>
          <option value="ne">er forskellig fra</option>
          <option value="gt">større end</option>
          <option value="gte">mindst</option>
          <option value="lt">mindre end</option>
          <option value="lte">højst</option>
          <option value="contains">indeholder</option>
          <option value="is_null">er tom</option>
          <option value="is_not_null">er udfyldt</option></select
        ><select
          v-if="
            fields.find((f) => f.id === filter.fieldId)?.type === 'boolean' &&
            !filter.op.startsWith('is_')
          "
          v-model="filter.value"
          aria-label="Filterværdi"
        >
          <option :value="true">Ja</option>
          <option :value="false">Nej</option></select
        ><input
          v-else-if="!filter.op.startsWith('is_')"
          v-model="filter.value"
          aria-label="Filterværdi"
          placeholder="Værdi"
        /><button
          type="button"
          class="icon-button"
          aria-label="Fjern filter"
          @click="query.filters.splice(i, 1)"
        >
          <DeleteIcon :size="16" />
        </button>
      </div>
    </div>
    <div class="query-bottom">
      <button
        type="button"
        class="text-button"
        @click="addFilter"
        :disabled="query.filters.length >= 20"
      >
        <AddIcon :size="16" />Tilføj filter</button
      ><select
        v-if="query.filters.length > 1"
        v-model="query.filterMode"
        aria-label="Filterkombination"
      >
        <option value="and">Alle betingelser</option>
        <option value="or">Mindst én betingelse</option>
      </select>
    </div>
    <details class="query-period">
      <summary>Periode og tidszone</summary>
      <button
        type="button"
        v-if="!query.period"
        class="text-button"
        @click="
          query.period = {
            fieldId:
              eligible.find((f) => ['date', 'datetime'].includes(f.type))?.id ??
              '',
            from: new Date(new Date().setUTCHours(0, 0, 0, 0)).toISOString(),
            to: new Date(new Date().setUTCHours(24, 0, 0, 0)).toISOString(),
            timezone: 'Europe/Copenhagen',
          }
        "
      >
        Afgræns en periode
      </button>
      <div v-else class="form-grid">
        <label
          >Datofelt<select v-model="query.period.fieldId">
            <option
              v-for="f in eligible.filter((f) =>
                ['date', 'datetime'].includes(f.type),
              )"
              :key="f.id"
              :value="f.id"
            >
              {{ f.label }}
            </option>
          </select></label
        ><label
          >Fra, inklusiv (ISO med tidszone)<input
            v-model="query.period.from"
            placeholder="2026-09-01T00:00:00+02:00" /></label
        ><label
          >Til, eksklusiv (ISO med tidszone)<input
            v-model="query.period.to"
            placeholder="2026-10-01T00:00:00+02:00" /></label
        ><label
          >Tidszone<input
            v-model="query.period.timezone"
            placeholder="Europe/Copenhagen" /></label
        ><button type="button" class="text-button" @click="delete query.period">
          Fjern periode
        </button>
      </div>
    </details>
  </div>
</template>
